import { MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/** Both offsets default to zero on the backend, i.e. the loads act centrically. */
const LOAD_ECCENTRICITY_DEFAULT: LoadEccentricity = { e_y: 0, e_z: 0 }

/**
 * True when the member check settings carry a load eccentricity, i.e. the
 * member is a timber rip or column (every timber member stores one; the
 * element-type gate decides where it is offered). Slab beams, steel columns
 * and "other" members do not.
 */
export const hasLoadEccentricitySettings = (
  settings: SettingsOnMember | undefined,
): settings is TimberCheckSettings => settings?.setting_type === 'timber'

export const getLoadEccentricitySettings = (
  settings: SettingsOnMember | undefined,
): LoadEccentricity | undefined =>
  settings && 'load_eccentricity' in settings ? settings.load_eccentricity : undefined

/**
 * Ensure the shared settings object always carries a load_eccentricity object
 * so the field component never reads it as null/undefined.
 */
export const normalizeLoadEccentricitySettings = (settings: SettingsOnMember): SettingsOnMember =>
  ({
    ...settings,
    load_eccentricity: getLoadEccentricitySettings(settings) || LOAD_ECCENTRICITY_DEFAULT,
  } as SettingsOnMember)

/**
 * Applies the staged shared load eccentricity to one member, leaving every
 * other part of its check settings untouched.
 */
export const mergeLoadEccentricitySettings = (
  existing: SettingsOnMember,
  shared: SettingsOnMember,
): SettingsOnMember =>
  ({
    ...existing,
    load_eccentricity: getLoadEccentricitySettings(shared) || LOAD_ECCENTRICITY_DEFAULT,
  } as SettingsOnMember)

const centimeterFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

/** The eccentricity is stored in meters, like everywhere else in the model. */
const formatCentimeters = (meters: number): string =>
  `${centimeterFormatter.format(meters * 100)} cm`

/** While a shared value is staged it is previewed for every row. */
export const buildLoadEccentricitySettingsRow = (
  element: MultiEditCheckSettingsElement,
  sharedSettings: SettingsOnMember | undefined,
): Record<string, string> => {
  const settings =
    getLoadEccentricitySettings(sharedSettings) ??
    getLoadEccentricitySettings(element.checkSettings) ??
    LOAD_ECCENTRICITY_DEFAULT

  return {
    eccentricityZ: formatCentimeters(settings.e_z),
    eccentricityY: formatCentimeters(settings.e_y),
  }
}
